"use client";
import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";

export type EventRoomTabsProps = {
  rooms: IResource[];
  selectedRoomId: string;
  onSelectRoom: (roomId: string) => void;
  /** Period of the reservation — every room of a reservation shares the same one. */
  timeLabel: string;
};

/**
 * Switches which room of a multi-room reservation the details pane is showing.
 * Rendered only when the reservation holds more than one room.
 */
export const EventRoomTabs: React.FC<EventRoomTabsProps> = ({
  rooms,
  selectedRoomId,
  onSelectRoom,
  timeLabel
}) => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const refreshScrollState = React.useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft < maxScrollLeft - 1);
  }, []);

  React.useEffect(() => {
    refreshScrollState();
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(refreshScrollState);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [refreshScrollState]);

  const scrollBy = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.8,
      behavior: "smooth"
    });
  };

  return (
    <div className="relative flex items-stretch border-neutral-200 border-b">
      {canScrollLeft && (
        <ScrollButton side="left" onClick={() => scrollBy(-1)} />
      )}
      <div
        ref={scrollerRef}
        onScroll={refreshScrollState}
        className="scrollbar-none flex flex-1 gap-10 overflow-x-auto"
      >
        {rooms.map((room) => {
          const isSelected = room.id === selectedRoomId;
          return (
            <button
              key={room.id}
              type="button"
              onClick={() => onSelectRoom(room.id)}
              className={cn(
                "-mb-px shrink-0 border-b-2 pb-2.5 text-left",
                isSelected
                  ? "border-neutral-1000"
                  : "border-transparent hover:border-neutral-300"
              )}
            >
              <div
                className={cn(
                  "font-bold text-base",
                  isSelected ? "text-neutral-1000" : "text-neutral-600"
                )}
              >
                {room.name}
              </div>
              <div className="text-neutral-500 text-sm">{timeLabel}</div>
            </button>
          );
        })}
      </div>
      {canScrollRight && (
        <ScrollButton side="right" onClick={() => scrollBy(1)} />
      )}
    </div>
  );
};

const ScrollButton: React.FC<{
  side: "left" | "right";
  onClick: () => void;
}> = ({ side, onClick }) => (
  <button
    type="button"
    aria-label={side === "left" ? "Previous rooms" : "More rooms"}
    onClick={onClick}
    className={cn(
      "flex shrink-0 items-center bg-background px-1 text-neutral-600 hover:text-neutral-1000",
      side === "left" ? "pr-2" : "pl-2"
    )}
  >
    {side === "left" ? (
      <ChevronLeft className="h-5 w-5" />
    ) : (
      <ChevronRight className="h-5 w-5" />
    )}
  </button>
);
